import { FileReference } from '@kaizenplatform/belfast-entity';
import produce from 'immer';

import State from '../core/State';
import CreateIssue from '../entity/CreateIssue';
import CreateIssueComment from '../entity/CreateIssueComment';
import UploadingFile from '../entity/UploadingFile';
import { toJSDate } from '../graphql/datetime';
import initialState from '../initialState';
import {
  ModificationRequestFeeType,
  ModificationRequestForDetailFragment,
  ModificationRequestItemForDetailFragment,
  ModificationRequestItemIssueCommentForDetailFragment,
  ModificationRequestItemIssueForDetailFragment,
} from './../generated/graphql';

export const startFetchingModificationRequest = () => (state: State): State => ({
  ...state,
  postedModificationRequests: {
    ...state.postedModificationRequests,
    fetching: state.postedModificationRequests.fetching + 1,
  },
});

export const finishFetchingModificationRequest = () => (state: State): State => ({
  ...state,
  postedModificationRequests: {
    ...state.postedModificationRequests,
    fetching: state.postedModificationRequests.fetching - 1,
  },
});

export const setAllModificationRequests = (modificationRequests: Array<ModificationRequestForDetailFragment>) => (
  state: State,
): State => ({
  ...state,
  postedModificationRequests: {
    ...state.postedModificationRequests,
    modificationRequests: modificationRequests,
  },
});

export const finishFetchingAssetFiles = (files: Array<FileReference>) => (state: State): State => {
  return {
    ...state,
    postedModificationRequests: {
      ...state.postedModificationRequests,
      assets: [...state.postedModificationRequests.assets, ...files],
    },
  };
};

export const setCreateIssue = (createIssues: Array<CreateIssue>) => (state: State): State => ({
  ...state,
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    createIssues: createIssues,
  },
});

export const setCreateIssueComment = (createIssueComments: Array<CreateIssueComment>) => (state: State): State => ({
  ...state,
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    createIssueComments: createIssueComments,
  },
});

export const addUploadingAssetFiles = (uploadingFiles: UploadingFile[]) => (state: State): State =>
  produce(state, (draft) => {
    draft.modificationRequestEditing.createModificationRequest.uploadingAssetFiles.push(...uploadingFiles);
  });

export const updateUploadingFilesProgress = (fileId: string, progress: number) => (state: State): State => {
  const progressFiles = state.modificationRequestEditing.createModificationRequest.uploadingFilesProgress;
  progressFiles[fileId] = { progress: progress };

  return {
    ...state,
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      createModificationRequest: {
        ...state.modificationRequestEditing.createModificationRequest,
        uploadingFilesProgress: progressFiles,
      },
    },
  };
};

export const removeUploadingFilesProgress = (fileId: string) => (state: State): State => {
  const progressFiles = state.modificationRequestEditing.createModificationRequest.uploadingFilesProgress;
  delete progressFiles[fileId];

  return {
    ...state,
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      createModificationRequest: {
        ...state.modificationRequestEditing.createModificationRequest,
        uploadingFilesProgress: progressFiles,
      },
    },
  };
};

export const removeUploadingAssetFile = (file: File) => (state: State): State => ({
  ...state,
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    createModificationRequest: {
      ...state.modificationRequestEditing.createModificationRequest,
      uploadingAssetFiles: state.modificationRequestEditing.createModificationRequest.uploadingAssetFiles.filter(
        (f) => f !== file,
      ),
    },
  },
});

export const addAssetFile = (fileReference: FileReference) => (state: State): State => {
  const assets = [...state.postedModificationRequests.assets, fileReference];
  const assetIds = [...state.modificationRequestEditing.createModificationRequest.assetIds, fileReference.id];

  return {
    ...state,
    postedModificationRequests: {
      ...state.postedModificationRequests,
      assets: assets,
    },
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      createModificationRequest: {
        ...state.modificationRequestEditing.createModificationRequest,
        assetIds: assetIds,
      },
    },
  };
};

export const removeAssetFile = (fileId: string) => (state: State): State => {
  return {
    ...state,
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      createModificationRequest: {
        ...state.modificationRequestEditing.createModificationRequest,
        assetIds: state.modificationRequestEditing.createModificationRequest.assetIds.filter((id) => id !== fileId),
      },
    },
  };
};

export const removeAllAssetFile = () => (state: State): State => {
  return {
    ...state,
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      createModificationRequest: {
        ...state.modificationRequestEditing.createModificationRequest,
        assetIds: initialState.modificationRequestEditing.createModificationRequest.assetIds,
        uploadingAssetFiles: initialState.modificationRequestEditing.createModificationRequest.uploadingAssetFiles,
        uploadingFilesProgress:
          initialState.modificationRequestEditing.createModificationRequest.uploadingFilesProgress,
      },
    },
  };
};

export const addUploadingEditAssetFiles = (uploadingFiles: UploadingFile[]) => (state: State): State => {
  return {
    ...state,
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      editModificationRequestItem: {
        ...state.modificationRequestEditing.editModificationRequestItem,
        uploadingAssetFiles: [
          ...state.modificationRequestEditing.editModificationRequestItem.uploadingAssetFiles,
          ...uploadingFiles,
        ],
      },
    },
  };
};

export const updateUploadingEditFilesProgress = (fileId: string, progress: number) => (state: State): State => {
  const progressFiles = state.modificationRequestEditing.editModificationRequestItem.uploadingFilesProgress;
  progressFiles[fileId] = { progress: progress };

  return {
    ...state,
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      editModificationRequestItem: {
        ...state.modificationRequestEditing.editModificationRequestItem,
        uploadingFilesProgress: progressFiles,
      },
    },
  };
};

export const addUploadedEditAssetFiles = (fileId: string) => (state: State): State => {
  return {
    ...state,
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      editModificationRequestItem: {
        ...state.modificationRequestEditing.editModificationRequestItem,
        uploadedAssetFileIds: [
          ...state.modificationRequestEditing.editModificationRequestItem.uploadedAssetFileIds,
          fileId,
        ],
      },
    },
  };
};

export const addEditAssetFile = (fileReference: FileReference) => (state: State): State => ({
  ...state,
  postedModificationRequests: {
    ...state.postedModificationRequests,
    assets: [...state.postedModificationRequests.assets, fileReference],
  },
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    editModificationRequestItem: {
      ...state.modificationRequestEditing.editModificationRequestItem,
      item: {
        ...state.modificationRequestEditing.editModificationRequestItem.item!,
        assetIds: [...state.modificationRequestEditing.editModificationRequestItem.item!.assetIds, fileReference.id],
      },
    },
  },
});

export const removeUploadingEditFilesProgress = (fileId: string) => (state: State): State => {
  const progressFiles = state.modificationRequestEditing.editModificationRequestItem.uploadingFilesProgress;
  delete progressFiles[fileId];

  return {
    ...state,
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      editModificationRequestItem: {
        ...state.modificationRequestEditing.editModificationRequestItem,
        uploadingFilesProgress: progressFiles,
      },
    },
  };
};

export const removeUploadingEditAssetFile = (file: File) => (state: State): State => ({
  ...state,
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    editModificationRequestItem: {
      ...state.modificationRequestEditing.editModificationRequestItem,
      uploadingAssetFiles: state.modificationRequestEditing.editModificationRequestItem.uploadingAssetFiles.filter(
        (f) => f !== file,
      ),
    },
  },
});

export const addUploadingIssueAssetFiles = (uploadingFiles: UploadingFile[], id: string) => (state: State): State =>
  produce(state, (draft) => {
    const index = draft.modificationRequestEditing.createIssues.findIndex((issue) => issue.itemId === id);
    draft.modificationRequestEditing.createIssues[index].uploadingAssetFiles.concat(uploadingFiles);
  });

export const updateUploadingIssueFilesProgress = (fileId: string, progress: number, id: string) => (
  state: State,
): State =>
  produce(state, (draft) => {
    const index = draft.modificationRequestEditing.createIssues.findIndex((issue) => issue.itemId === id);
    const progressFiles = draft.modificationRequestEditing.createIssues[index].uploadingFilesProgress;
    progressFiles[fileId] = { progress: progress };
    draft.modificationRequestEditing.createIssues[index].uploadingFilesProgress = progressFiles;
  });

export const removeUploadingIssueFilesProgress = (fileId: string, id: string) => (state: State): State =>
  produce(state, (draft) => {
    const index = draft.modificationRequestEditing.createIssues.findIndex((issue) => issue.itemId === id);
    const progressFiles = draft.modificationRequestEditing.createIssues[index].uploadingFilesProgress;
    delete progressFiles[fileId];
    draft.modificationRequestEditing.createIssues[index].uploadingFilesProgress = progressFiles;
  });

export const removeUploadingIssueAssetFile = (file: File, id: string) => (state: State): State =>
  produce(state, (draft) => {
    const index = draft.modificationRequestEditing.createIssues.findIndex((issue) => issue.itemId === id);
    const createIssue = draft.modificationRequestEditing.createIssues[index];
    const files = createIssue.uploadingAssetFiles.filter((f) => f !== file);
    createIssue.uploadingAssetFiles = files;
  });

export const resetUploadingIssueAssetFile = (index: number) => (state: State): State =>
  produce(state, (draft) => {
    draft.modificationRequestEditing.createIssues[index].assetIds = [];
  });

export const addUploadingIssueCommentAssetFiles = (uploadingFiles: UploadingFile[], id: string) => (
  state: State,
): State =>
  produce(state, (draft) => {
    const index = draft.modificationRequestEditing.createIssueComments.findIndex((comment) => comment.issueId === id);
    draft.modificationRequestEditing.createIssueComments[index].uploadingAssetFiles.concat(uploadingFiles);
  });

export const updateUploadingIssueCommentFilesProgress = (fileId: string, progress: number, id: string) => (
  state: State,
): State =>
  produce(state, (draft) => {
    const index = draft.modificationRequestEditing.createIssueComments.findIndex((comment) => comment.issueId === id);
    draft.modificationRequestEditing.createIssueComments[index].uploadingFilesProgress[fileId] = { progress: progress };
  });

export const removeUploadingIssueCommentFilesProgress = (fileId: string, id: string) => (state: State): State =>
  produce(state, (draft) => {
    const index = draft.modificationRequestEditing.createIssueComments.findIndex((comment) => comment.issueId === id);
    const progressFiles = draft.modificationRequestEditing.createIssueComments[index].uploadingFilesProgress;
    delete progressFiles[fileId];
    draft.modificationRequestEditing.createIssueComments[index].uploadingFilesProgress = progressFiles;
  });

export const removeUploadingIssueCommentAssetFile = (file: File, id: string) => (state: State): State =>
  produce(state, (draft) => {
    const index = draft.modificationRequestEditing.createIssueComments.findIndex((comment) => comment.issueId === id);
    const createIssueComment = draft.modificationRequestEditing.createIssueComments[index];
    createIssueComment.uploadingAssetFiles = createIssueComment.uploadingAssetFiles.filter((f) => f !== file);
  });

export const resetUploadingIssueCommentAssetFile = (index: number) => (state: State): State =>
  produce(state, (draft) => {
    draft.modificationRequestEditing.createIssueComments[index].assetIds = [];
  });

export const addAssetIssueFile = (fileReference: FileReference, id: string) => (state: State): State =>
  produce(state, (draft) => {
    draft.postedModificationRequests.assets.push(fileReference);
    const index = draft.modificationRequestEditing.createIssues.findIndex((issue) => issue.itemId === id);
    draft.modificationRequestEditing.createIssues[index].assetIds.push(fileReference.id);
  });

export const removeAssetIssueFile = (fileId: string, id: string) => (state: State): State =>
  produce(state, (draft) => {
    const index = draft.modificationRequestEditing.createIssues.findIndex((issue) => issue.itemId === id);
    const createIssue = draft.modificationRequestEditing.createIssues[index];
    createIssue.assetIds = createIssue.assetIds.filter((id) => id !== fileId);
  });

export const addAssetIssueCommentFile = (fileReference: FileReference, id: string) => (state: State): State =>
  produce(state, (draft) => {
    draft.postedModificationRequests.assets.push(fileReference);
    const index = draft.modificationRequestEditing.createIssueComments.findIndex((comment) => comment.issueId === id);
    draft.modificationRequestEditing.createIssueComments[index].assetIds.push(fileReference.id);
  });

export const removeAssetIssueCommentFile = (fileId: string, id: string) => (state: State): State =>
  produce(state, (draft) => {
    const index = draft.modificationRequestEditing.createIssueComments.findIndex((comment) => comment.issueId === id);
    const createIssue = draft.modificationRequestEditing.createIssueComments[index];
    createIssue.assetIds = createIssue.assetIds.filter((id) => id !== fileId);
  });

export const setSelectedDeliverable = (deliverableId: string) => (state: State): State => ({
  ...state,
  postedModificationRequests: {
    ...state.postedModificationRequests,
    selectedId: deliverableId,
  },
});

export const setSelectedFeeType = (feeType: ModificationRequestFeeType) => (state: State): State => ({
  ...state,
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    createModificationRequest: {
      ...state.modificationRequestEditing.createModificationRequest,
      feeType: feeType,
    },
  },
});

export const setModificationRequestItems = (items: Array<ModificationRequestItemForDetailFragment>) => (
  state: State,
): State => ({
  ...state,
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    createModificationRequest: {
      ...state.modificationRequestEditing.createModificationRequest,
      items: items,
    },
  },
});

export const setModificationRequestIssue = (
  modificationRequestIndex: number,
  modificationRequestItemIndex: number,
  modificationRequestIssue: ModificationRequestItemIssueForDetailFragment,
) => (state: State): State =>
  produce(state, (draft) => {
    draft.postedModificationRequests.modificationRequests![modificationRequestIndex].items[
      modificationRequestItemIndex
    ].issue = modificationRequestIssue;
  });

export const setModificationRequestIssueComment = (
  modificationRequestIndex: number,
  modificationRequestItemIndex: number,
  modificationRequestIssueComment: ModificationRequestItemIssueCommentForDetailFragment,
) => (state: State): State =>
  produce(state, (draft) => {
    const issue = draft.postedModificationRequests.modificationRequests![modificationRequestIndex].items[
      modificationRequestItemIndex
    ].issue;
    issue?.comments.push(modificationRequestIssueComment);
  });

export const setResolveModificationRequestIssue = (
  modificationRequestIndex: number,
  modificationRequestItemIndex: number,
) => (state: State): State =>
  produce(state, (draft) => {
    draft.postedModificationRequests.modificationRequests![modificationRequestIndex].items[
      modificationRequestItemIndex
    ].issue!.resolved = true;
  });

export const setReopenModificationRequestIssue = (
  modificationRequestIndex: number,
  modificationRequestItemIndex: number,
  modificationRequestIssue: ModificationRequestItemIssueForDetailFragment,
) => (state: State): State =>
  produce(state, (draft) => {
    draft.postedModificationRequests.modificationRequests![modificationRequestIndex].items[
      modificationRequestItemIndex
    ].issue = modificationRequestIssue;
  });

export const addModificationRequestToEditing = (modificationRequest: ModificationRequestForDetailFragment) => (
  state: State,
): State => {
  const { id, bucketId, items, feeType, deliveryDue } = modificationRequest;

  return {
    ...state,
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      createModificationRequest: {
        ...state.modificationRequestEditing.createModificationRequest,
        id: id,
        bucketId: bucketId || '',
        items: items,
        feeType: feeType,
        deliveryDue: toJSDate(deliveryDue),
      },
    },
  };
};

export const addModificationRequestItem = (newModificationRequestItem: ModificationRequestItemForDetailFragment) => (
  state: State,
): State => {
  return {
    ...state,
    modificationRequestEditing: {
      ...state.modificationRequestEditing,
      createModificationRequest: {
        ...state.modificationRequestEditing.createModificationRequest,
        items: [...state.modificationRequestEditing.createModificationRequest.items, newModificationRequestItem],
        assetIds: [],
        uploadingAssetFiles: [],
        uploadingFilesProgress: {},
      },
    },
  };
};

export const setEditModificationRequestItem = (modificationRequestItem: ModificationRequestItemForDetailFragment) => (
  state: State,
): State => ({
  ...state,
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    editModificationRequestItem: {
      ...state.modificationRequestEditing.editModificationRequestItem,
      item: modificationRequestItem,
      uploadedAssetFileIds: initialState.modificationRequestEditing.editModificationRequestItem.uploadedAssetFileIds,
    },
  },
});

export const removeEditAssetFile = (fileId: string) => (state: State): State => ({
  ...state,
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    editModificationRequestItem: {
      ...state.modificationRequestEditing.editModificationRequestItem,
      item: {
        ...state.modificationRequestEditing.editModificationRequestItem.item!,
        assetIds: state.modificationRequestEditing.editModificationRequestItem.item!.assetIds.filter(
          (id) => id !== fileId,
        ),
      },
    },
  },
});

export const disposeEditModificationRequestItem = () => (state: State): State => ({
  ...state,
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    editModificationRequestItem: initialState.modificationRequestEditing.editModificationRequestItem,
  },
});

export const disposeEditing = () => (state: State): State => ({
  ...state,
  modificationRequestEditing: {
    ...state.modificationRequestEditing,
    createModificationRequest: {
      ...initialState.modificationRequestEditing.createModificationRequest,
    },
  },
});

export const setModificationRequest = (
  modificationRequestIndex: number,
  modificationRequest: ModificationRequestForDetailFragment,
) => (state: State): State =>
  produce(state, (draft) => {
    draft.postedModificationRequests.modificationRequests![modificationRequestIndex] = modificationRequest;
  });

export const dispose = () => (state: State): State => ({
  ...state,
  postedModificationRequests: {
    ...initialState.postedModificationRequests,
  },
  modificationRequestEditing: {
    ...initialState.modificationRequestEditing,
  },
});

export const updateModificationRequestItem = (
  index: number,
  modificationRequestItem: ModificationRequestItemForDetailFragment,
) => (state: State): State =>
  produce(state, (draft) => {
    draft.modificationRequestEditing.createModificationRequest.items[index] = modificationRequestItem;
  });
